import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Users, Settings, Home, PlusCircle, FileText, X } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../contexts/auth/hooks';
import ChargeCustomerFlow from './ChargeCustomerFlow';
import { NewAppointmentFlow } from './appointments/NewAppointmentFlow';

type NavItem = { icon: React.ComponentType<any>; label: string; path: string };

const NAV_HEIGHT_PX = 64;

const navLeft: NavItem[] = [
  { icon: Home, label: 'דשבורד', path: '/dashboard' },
  { icon: Calendar, label: 'יומן', path: '/calendar' },
];
const navRight: NavItem[] = [
  { icon: Users, label: 'לקוחות', path: '/customers' },
  { icon: Settings, label: 'הגדרות', path: '/settings' },
];

const fabItems = [
  { icon: Calendar, label: 'תור חדש', color: 'bg-indigo-600', requiredFeature: null, type: 'appointment' as const },
  { icon: Users, label: 'לקוח חדש', color: 'bg-emerald-600', requiredFeature: null, type: 'navigate' as const, navigateTo: '/customers', state: { openNewCustomerModal: true } },
  { icon: FileText, label: 'חייב לקוח', color: 'bg-purple-600', requiredFeature: null, type: 'charge' as const },
];

function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isFeatureAvailable, subscription } = useSubscription();
  const { business } = useAuth();

  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentModalProps, setAppointmentModalProps] = useState<{
    initialCustomerId?: string;
    initialDate?: Date;
    initialStaffId?: string;
    fromCalendar?: boolean;
  }>({});
  const [hideFabByDrawer, setHideFabByDrawer] = useState(false);
  const [hideByAIChat, setHideByAIChat] = useState(false);
  const [hideByServiceModal, setHideByServiceModal] = useState(false);
  const [hideByTerminalDialog, setHideByTerminalDialog] = useState(false);
  const [hideBySupportModal, setHideBySupportModal] = useState(false);
  const [hideByAppointmentModal, setHideByAppointmentModal] = useState(false);
  const [hideByCustomerModal, setHideByCustomerModal] = useState(false);

  useEffect(() => {
    const onDrawer = (e: any) => {
      const open = !!e?.detail?.open;
      setHideFabByDrawer(open);
      if (open) setShowFabMenu(false);
    };
    const onAIChat = (e: any) => {
      const open = !!e?.detail?.open;
      setHideByAIChat(open);
      if (open) setShowFabMenu(false);
    };
    const onServiceModal = (e: any) => {
      const open = !!e?.detail?.open;
      setHideByServiceModal(open);
      if (open) setShowFabMenu(false);
    };
    const onTerminalDialog = (e: any) => {
      const open = !!e?.detail?.open;
      setHideByTerminalDialog(open);
      if (open) setShowFabMenu(false);
    };
    const onSupportModal = (e: any) => {
      const open = !!e?.detail?.open;
      setHideBySupportModal(open);
      if (open) setShowFabMenu(false);
    };
    const onAppointmentModal = (e: any) => {
      const open = !!e?.detail?.open;
      setHideByAppointmentModal(open);
      if (open) setShowFabMenu(false);
    };
    const onCustomerModal = (e: any) => {
      const open = !!e?.detail?.open;
      setHideByCustomerModal(open);
      if (open) setShowFabMenu(false);
    };
    const onOpenNewAppointment = (e: any) => {
      const { initialCustomerId, initialDate, initialStaffId, fromCalendar } = e?.detail || {};
      setAppointmentModalProps({ initialCustomerId, initialDate, initialStaffId, fromCalendar });
      setShowAppointmentModal(true);
      setShowFabMenu(false);
    };
    window.addEventListener('ui:drawer', onDrawer);
    window.addEventListener('ui:aichat', onAIChat);
    window.addEventListener('ui:service-modal', onServiceModal);
    window.addEventListener('ui:terminal-dialog', onTerminalDialog);
    window.addEventListener('ui:support-modal', onSupportModal);
    window.addEventListener('ui:appointment-modal', onAppointmentModal);
    window.addEventListener('ui:customer-modal', onCustomerModal);
    window.addEventListener('ui:open-new-appointment', onOpenNewAppointment);
    return () => {
      window.removeEventListener('ui:drawer', onDrawer);
      window.removeEventListener('ui:aichat', onAIChat);
      window.removeEventListener('ui:service-modal', onServiceModal);
      window.removeEventListener('ui:terminal-dialog', onTerminalDialog);
      window.removeEventListener('ui:support-modal', onSupportModal);
      window.removeEventListener('ui:appointment-modal', onAppointmentModal);
      window.removeEventListener('ui:customer-modal', onCustomerModal);
      window.removeEventListener('ui:open-new-appointment', onOpenNewAppointment);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('has-bottom-nav');
    return () => document.documentElement.classList.remove('has-bottom-nav');
  }, []);
useEffect(() => {
  const root = document.documentElement;
  if (showFabMenu) root.classList.add('blur-app');
  else root.classList.remove('blur-app');
  return () => root.classList.remove('blur-app');
}, [showFabMenu]);

  const availableFabItems = useMemo(
    () =>
      fabItems.map((item) => ({
        ...item,
        disabled: item.label === 'חייב לקוח' && subscription?.plan?.code === 'basic', // חסום אם זו חבילה בסיסית
      })),
    [subscription]
  );

  const isSettingsSubpage = location.pathname.startsWith('/settings/');
  const isCustomersSubpage = location.pathname.startsWith('/customers/');
  const isAppointmentsSubpage = location.pathname.startsWith('/appointments/');

  const isActivePath = (path: string) =>
    location.pathname === path ||
    (isSettingsSubpage && path === '/settings') ||
    (isCustomersSubpage && path === '/customers') ||
    (isAppointmentsSubpage && path === '/appointments');

  const fabMenuBottom = `calc(${NAV_HEIGHT_PX}px + env(safe-area-inset-bottom) + 32px)`;

  const nav = (
  <nav
    className="bottom-nav-fixed"
    style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
     height: `calc(${NAV_HEIGHT_PX}px + max(calc(env(safe-area-inset-bottom) - 50px), 0px))`,
      zIndex: (hideByAIChat || hideByServiceModal || hideByTerminalDialog || hideBySupportModal || hideByAppointmentModal || hideByCustomerModal) ? -1 : 2147483647,
      transform: (hideByAIChat || hideByServiceModal || hideByTerminalDialog || hideBySupportModal || hideByAppointmentModal || hideByCustomerModal) ? 'translateY(100%)' : 'translateY(0)',
      transition: 'transform 0.3s ease-in-out, z-index 0s',
      background: 'var(--surface)',
      borderTop: '1px solid var(--border)',
      boxShadow: '0 -2px 8px rgba(17,24,39,0.06)',
      WebkitTransform: 'translateZ(0)',
      paddingBottom: 'max(calc(env(safe-area-inset-bottom) - 50px), 0px)',
      overflow: 'visible',
    }}
   >
    {/* ממלא את ה-safe-area (לא תופס לחיצות) */}
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 'env(safe-area-inset-bottom)',              // ← לא 0 !
        background: 'var(--surface)',
        pointerEvents: 'none',
      }}
    />

      {/* התוכן של הניווט יושב בדיוק מעל ה-safe-area */}
    <div
      className="max-w-lg mx-auto px-4"
      style={{
        height: NAV_HEIGHT_PX,
        position: 'absolute',
        left: 0,
        right: 0,
      bottom: 'max(calc(env(safe-area-inset-bottom) - 24px), 0px)', // ← כאן יושבים האייקונים
      }}
    >
        <div className="relative flex items-center justify-between h-full">
          <div className="flex items-center gap-6">
            {navLeft.map(item => (
              <NavButton key={item.path} item={item} active={isActivePath(item.path)} />
            ))}
          </div>

          <div
  className="flex items-center justify-center w-[72px] shrink-0 relative z-[101]"
  style={{ transform: 'translateY(-20px)' }} // ← מרים ~20px מעל הבר
>
        {!hideFabByDrawer && (
    <motion.button
      type="button"
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.96 }}
      onClick={() => setShowFabMenu(v => !v)}
      className="flex items-center justify-center text-white p-4 rounded-full shadow-xl ring-1 ring-black/5 select-none"
      aria-label="תפריט פעולות"
      style={{
        background: 'var(--brand)',
        boxShadow: '0 10px 24px rgba(0,0,0,.18)',
      }}
    >
      <motion.span
                  animate={{ rotate: showFabMenu ? 180 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        style={{ display: 'inline-flex' }}
      >
        {showFabMenu ? <X className="h-7 w-7" /> : <PlusCircle className="h-7 w-7" />}
      </motion.span>
    </motion.button>
  )}
</div>

          <div className="flex items-center gap-6">
            {navRight.map(item => (
              <NavButton key={item.path} item={item} active={isActivePath(item.path)} />
            ))}
          </div>
        </div>
      </div>

      {/* תפריט FAB */}
      <AnimatePresence>
        {showFabMenu && availableFabItems.length > 0 && (
          <>
          {/* overlay — לא מכסה את איזור ה-nav */}
<motion.div
  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
  className="fixed inset-x-0 top-0 bg-black/30 backdrop-blur-sm overscroll-contain touch-none"
  style={{
    bottom: `calc(${NAV_HEIGHT_PX}px + env(safe-area-inset-bottom))`, // חתוך בדיוק מעל ה-nav
    zIndex: 2147483100,            // מעל התוכן, מתחת לתפריט ה-FAB
    pointerEvents: 'auto',
  }}
  onClick={() => setShowFabMenu(false)}
  onWheel={(e) => e.preventDefault()}
  onTouchMove={(e) => e.preventDefault()}
  data-overlay
/>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 280, damping: 24 }}
              className="fixed inset-x-0 px-4"
              style={{ bottom: fabMenuBottom, zIndex: 2147483200 }}
            >
              <div className="max-w-lg mx-auto flex flex-col items-center gap-4">
                {availableFabItems.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 20, delay: index * 0.06 } }}
                    exit={{ opacity: 0, scale: 0.8, y: 20, transition: { duration: 0.18, delay: (availableFabItems.length - 1 - index) * 0.05 } }}
                  >
                    {item.type === 'appointment' ? (
                      <button
                        type="button"
                        className={`flex items-center gap-3 w-full ${item.color} text-white p-4 rounded-2xl shadow-sm font-medium justify-center`}
                        onClick={() => {
                          setShowFabMenu(false);
                          setShowAppointmentModal(true);
                        }}
                      >
                        <item.icon className="h-6 w-6" />
                        <span>{item.label}</span>
                      </button>
                    ) : item.type === 'navigate' ? (
                      <button
                        type="button"
                        className={`flex items-center gap-3 w-full ${item.color} text-white p-4 rounded-2xl shadow-sm font-medium justify-center`}
                        onClick={() => {
                          setShowFabMenu(false);
                          navigate(item.navigateTo!, { state: item.state });
                        }}
                      >
                        <item.icon className="h-6 w-6" />
                        <span>{item.label}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`flex items-center gap-3 w-full ${item.color} text-white p-4 rounded-2xl shadow-sm font-medium justify-center ${
                          item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={() => {
                          if (!item.disabled) {
                            setShowFabMenu(false);
                            setShowChargeDialog(true);
                          }
                        }}
                        disabled={item.disabled}
                      >
                        <item.icon className="h-6 w-6" />
                        <span>{item.label}</span>
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {showChargeDialog && (
        <Portal>
          <ChargeCustomerFlow
            open={showChargeDialog}
            onClose={() => setShowChargeDialog(false)}
            businessId={business?.id}
          />
        </Portal>
      )}

      {showAppointmentModal && (
        <Portal>
          <NewAppointmentFlow
            onClose={() => {
              setShowAppointmentModal(false);
              setAppointmentModalProps({});
            }}
            onSuccess={() => {
              setShowAppointmentModal(false);
              setAppointmentModalProps({});
            }}
            {...appointmentModalProps}
          />
        </Portal>
      )}
    </nav>
  );

  const portalRoot = document.getElementById('portal-root') || document.body;
  return ReactDOM.createPortal(nav, portalRoot);
}

function NavButton({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(item.path)}
      className="relative flex flex-col items-center justify-center min-w-[64px]"
      style={{ height: 64 }}
    >
      <motion.div
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.95 }}
        className={`flex flex-col items-center ${active ? 'text-indigo-600' : 'text-gray-500'}`}
        style={{ transform: 'translateY(6px)' }}
      >
        <Icon className="h-7 w-7" />
        <span className="text-xs mt-1 select-none">{item.label}</span>
        {active && (
          <motion.div
            layoutId="activeTab"
            className="absolute -bottom-1 w-10 h-0.5 bg-indigo-600 rounded-full pointer-events-none"
          />
        )}
      </motion.div>
    </button>
  );
}

function Portal({ children }: { children: React.ReactNode }) {
  const portalRoot = document.getElementById('portal-root') || document.body;
  return ReactDOM.createPortal(children, portalRoot);
}

export default BottomNav;
